import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { DATA_TABLE_ROW_HEIGHT_CLASS } from '@odo/shared-ui';

// Viewport-based page size (same pattern as the Home patient table): fit as
// many rows as the visible table area allows so a page never scrolls.
// Attach the refs to the table card, thead, tbody, and pagination wrapper,
// and call remeasure once real rows are rendered (the loading row has a
// different height than data rows).

const CARD_BOTTOM_SPACE = 34; // the card's pb-8 wrapper padding (32px) + borders
const FALLBACK_ROW_HEIGHT = 56;

// Tailwind's h-N is N * 0.25rem, 4px at the default root size.
export function heightClassPx(className: `h-${number}`): number {
    return Number(className.slice(2)) * 4;
}

export const DATA_TABLE_ROW_HEIGHT = heightClassPx(DATA_TABLE_ROW_HEIGHT_CLASS);
// The h-16 override some tables pass.
export const DATA_TABLE_TALL_ROW_HEIGHT = 64;

interface ViewportPageSize {
    pageSize: number;
    tableCardRef: React.RefObject<HTMLDivElement>;
    tableHeaderRef: React.RefObject<HTMLTableSectionElement>;
    tableBodyRef: React.RefObject<HTMLTableSectionElement>;
    paginationRef: React.RefObject<HTMLDivElement>;
    remeasure: () => void;
    // One-shot remeasure for the "first real rows rendered" layout effects.
    remeasureOnce: () => void;
    // True once the mount measurement ran; gate the first fetch on it.
    isMeasured: boolean;
}

// Pass `rowHeight` when every row has a known height: the mount measurement is
// then final, so the first fetch can wait for it instead of refetching later.
export function useViewportPageSize(
    defaultPageSize = 20,
    rowHeight?: number,
    bottomSpace = CARD_BOTTOM_SPACE
): ViewportPageSize {
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [isMeasured, setIsMeasured] = useState(false);

    const tableCardRef = useRef<HTMLDivElement>(null);
    const tableHeaderRef = useRef<HTMLTableSectionElement>(null);
    const tableBodyRef = useRef<HTMLTableSectionElement>(null);
    const paginationRef = useRef<HTMLDivElement>(null);

    const remeasure = useCallback(() => {
        if (!tableCardRef.current || !tableHeaderRef.current || !tableBodyRef.current) return;
        const firstRow = tableBodyRef.current.querySelector('tr');
        if (!rowHeight && !firstRow) return;
        const cardTop = tableCardRef.current.getBoundingClientRect().top;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const headerHeight = tableHeaderRef.current.offsetHeight;
        const paginationHeight = paginationRef.current?.offsetHeight || 0;
        const resolvedRowHeight = rowHeight || firstRow?.offsetHeight || FALLBACK_ROW_HEIGHT;
        const availableHeight = Math.max(
            0,
            viewportHeight - cardTop - headerHeight - paginationHeight - bottomSpace
        );
        setPageSize(Math.max(1, Math.floor(availableHeight / resolvedRowHeight)));
    }, [rowHeight, bottomSpace]);

    // Pages remeasure when real data rows first land (the loading row's height
    // skews the initial measurement) - but only ONCE. Remeasuring on every
    // row-count change feeds pageSize -> refetch -> row count -> remeasure back
    // into itself, which can oscillate between two page sizes forever
    // (React error #185, maximum update depth).
    const measuredRealRows = useRef(false);
    const remeasureOnce = useCallback(() => {
        if (measuredRealRows.current) return;
        measuredRealRows.current = true;
        remeasure();
    }, [remeasure]);

    useLayoutEffect(() => {
        remeasure();
        setIsMeasured(true);
        window.addEventListener('resize', remeasure);
        return () => window.removeEventListener('resize', remeasure);
    }, [remeasure]);

    return {
        pageSize,
        tableCardRef,
        tableHeaderRef,
        tableBodyRef,
        paginationRef,
        remeasure,
        remeasureOnce,
        isMeasured
    };
}
